// App-wide chat socket lifecycle. Mounted inside AppLayout so the inbox
// subscription (unread bumps) stays live on every authenticated page.
// Routes socket events into the React Query cache; the socket itself
// (chatSocket singleton) stays React-free.

import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useIsAuthenticated } from "@/stores/authStore";
import { customKeys } from "@/lib/queryKeyFactory";
import { chatSocket, type ChatSocketStatus } from "../services/chatSocket";

const ACCESS_TOKEN_KEY = "tr_access_token";

interface ChatSocketContextValue {
  status: ChatSocketStatus;
}

const ChatSocketContext = createContext<ChatSocketContextValue>({ status: "disconnected" });

export function ChatSocketProvider({ children }: { children: ReactNode }) {
  const isAuthenticated = useIsAuthenticated();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<ChatSocketStatus>(chatSocket.getStatus());

  // Connect/disconnect with authentication state
  useEffect(() => {
    if (!isAuthenticated) {
      chatSocket.disconnect();
      return;
    }

    const token = localStorage.getItem(ACCESS_TOKEN_KEY);
    if (token) {
      chatSocket.connect(token);
    }

    return () => {
      chatSocket.disconnect();
    };
  }, [isAuthenticated]);

  // Status mirror + inbox events + reconnect gap-fill
  useEffect(() => {
    const offStatus = chatSocket.onStatusChange(setStatus);

    const offInbox = chatSocket.subscribeInbox((event) => {
      // INBOX_BUMP / THREAD_ARCHIVED — refresh the threads list (cheap, low-frequency)
      void queryClient.invalidateQueries({ queryKey: customKeys.chat.threads() });
      // Also mark the bumped thread's message history stale, so opening it
      // later refetches instead of serving a stale cache (live events are only
      // pushed into the cache of the currently OPEN thread).
      void queryClient.invalidateQueries({
        queryKey: customKeys.chat.messages(event.body.threadId),
      });
    });

    const offReconnect = chatSocket.onReconnect(() => {
      // REST is the source of truth: refetch threads + any open message caches
      void queryClient.invalidateQueries({ queryKey: customKeys.chat.threads() });
      void queryClient.invalidateQueries({ queryKey: [...customKeys.chat.all, "messages"] });
    });

    return () => {
      offStatus();
      offInbox();
      offReconnect();
    };
  }, [queryClient]);

  return <ChatSocketContext.Provider value={{ status }}>{children}</ChatSocketContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useChatSocket(): ChatSocketContextValue {
  return useContext(ChatSocketContext);
}
